import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mharher_token'));
  const [loading, setLoading] = useState(true);
  const isRefreshing = useRef(false);
  const refreshSubscribers = useRef([]);

  // Create axios instance with interceptors
  const api = axios.create({
    baseURL: API,
  });

  // Request interceptor - add token to all requests
  api.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('mharher_token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  // Response interceptor - handle 401 errors
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      // If 401 and not already retrying
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        
        // Check if token exists but expired
        const currentToken = localStorage.getItem('mharher_token');
        if (currentToken) {
          // Try to verify if token is truly expired or just invalid
          try {
            const tokenData = JSON.parse(atob(currentToken.split('.')[1]));
            const expiryTime = tokenData.exp * 1000;
            const now = Date.now();
            
            // If token expired, logout user gracefully
            if (now >= expiryTime) {
              console.log('Token expired, logging out...');
              localStorage.removeItem('mharher_token');
              setToken(null);
              setUser(null);
              toast.error('Session expirée. Veuillez vous reconnecter.');
              window.location.href = '/auth';
              return Promise.reject(error);
            }
          } catch (e) {
            // Invalid token format
            console.error('Invalid token format');
          }
        }
        
        // No valid token, redirect to login
        localStorage.removeItem('mharher_token');
        setToken(null);
        setUser(null);
        return Promise.reject(error);
      }
      
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    const currentToken = localStorage.getItem('mharher_token');
    if (!currentToken) {
      setLoading(false);
      return;
    }
    
    // Check token expiry before making request
    try {
      const tokenData = JSON.parse(atob(currentToken.split('.')[1]));
      const expiryTime = tokenData.exp * 1000;
      const now = Date.now();
      const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
      
      if (now >= expiryTime - bufferTime) {
        console.log('Token expired or about to expire');
        localStorage.removeItem('mharher_token');
        setToken(null);
        setLoading(false);
        return;
      }
    } catch (e) {
      console.error('Error parsing token:', e);
      localStorage.removeItem('mharher_token');
      setToken(null);
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('mharher_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
    
    // Set up periodic token check (every 5 minutes)
    const tokenCheckInterval = setInterval(() => {
      const currentToken = localStorage.getItem('mharher_token');
      if (currentToken) {
        try {
          const tokenData = JSON.parse(atob(currentToken.split('.')[1]));
          const expiryTime = tokenData.exp * 1000;
          const now = Date.now();
          const warningTime = 30 * 60 * 1000; // 30 minutes before expiry
          
          if (now >= expiryTime) {
            // Token expired
            localStorage.removeItem('mharher_token');
            setToken(null);
            setUser(null);
            toast.error('Session expirée. Veuillez vous reconnecter.');
          } else if (now >= expiryTime - warningTime) {
            // Token about to expire - show warning
            const minutesLeft = Math.round((expiryTime - now) / 60000);
            toast.warning(`Votre session expire dans ${minutesLeft} minutes.`);
          }
        } catch (e) {
          // Invalid token
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => clearInterval(tokenCheckInterval);
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('mharher_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, username, password, displayName, tiktokUsername, instagramUsername, youtubeUsername, captchaToken) => {
    const response = await api.post('/auth/register', {
      email,
      username,
      password,
      display_name: displayName || username,
      tiktok_username: tiktokUsername || null,
      instagram_username: instagramUsername || null,
      youtube_username: youtubeUsername || null,
      captcha_token: captchaToken || null,
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('mharher_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('mharher_token');
    setToken(null);
    setUser(null);
  };

  const setAuthToken = useCallback(async (newToken) => {
    localStorage.setItem('mharher_token', newToken);
    setToken(newToken);
    try {
      const response = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setUser(response.data);
    } catch (e) {
      console.error('Failed to fetch user after OAuth:', e);
    }
  }, []);

  const updateUser = (updates) => {
    setUser((prev) => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        updateUser,
        setAuthToken,
        api,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
